/*
  Pink cursor follower on product card images.

  A bougainvillea disc that trails the pointer across a card's photograph and
  locks onto a size when it reaches one. No library: CLAUDE.md bans those, and
  what a library would give us here is a spring, which is forty lines of
  arithmetic.

  Why a spring rather than CSS.

  A transition or a keyframe replays the same curve no matter what happened.
  Flick the pointer across the whole card and it takes the same path, at the
  same speed, with the same overshoot, as nudging it two pixels. That reads as a
  bounce, because it is one: a fixed animation triggered by an event.

  A spring has no duration. It is a position, a velocity and a target, and every
  frame it accelerates toward the target and loses a little energy. Move fast and
  it lags further, overshoots further and takes longer to settle. Move slowly and
  it barely deviates. Nothing is scripted, so nothing repeats, and that is the
  difference between fluid and bouncy.

  The squash is the other half. Stretching the disc along whichever axis it is
  travelling, in proportion to how fast it is going, is what makes it read as a
  volume of liquid rather than a shape being moved. It falls out of the spring
  for free, because the spring already knows its velocity.

  Design notes:
  - Delegated on document rather than bound per card, so the collection template
    and any future grid get it for free.
  - The disc is created lazily on first hover of a given card, so a page nobody
    hovers ships no extra nodes.
  - Mouse only, and only where the pointer is fine. A touch device has no cursor
    to follow, and pen input would fire this on every tap.
  - Skipped entirely for anyone who has asked for reduced motion. This is
    decoration, so the correct amount of it under that setting is none.
  - The loop stops itself once the spring is at rest, and restarts when the
    target moves, so a card sitting under a still pointer costs nothing.
*/
(function () {
  var FINE = '(hover: hover) and (pointer: fine)';
  var CALM = '(prefers-reduced-motion: reduce)';

  /*
    Spring constants. STIFF is how hard it pulls toward the target, DAMP is how
    much velocity survives each frame, so a lower DAMP is more damping.

    These are damped close to critical on purpose. For this integrator the
    boundary sits near DAMP = 1 - 2 * sqrt(STIFF), which is about 0.2 here, and
    anything far above it oscillates: the first tuning ran at 0.78 and 0.7 and
    the disc swung past its target four times before settling. That is a bounce,
    which is the thing a spring is supposed to avoid. Held slightly under-damped
    so there is one soft overshoot and no second one.

    Position is looser than scale, so the disc trails the pointer rather than
    sticking to it, and a size lock resolves without wallowing.
  */
  var STIFF = 0.16;
  var DAMP = 0.62;
  var SCALE_STIFF = 0.22;
  var SCALE_DAMP = 0.55;

  /* Velocity to stretch. At a hard flick the disc runs about 40px a frame, so
     0.009 puts the peak near a quarter, which is as far as it can go before it
     stops reading as the same object. */
  var SQUASH = 0.009;
  var SQUASH_CAP = 0.26;

  /* Below this speed there is no meaningful direction of travel, so the disc
     unwinds to level rather than holding whatever angle it stopped at. The ease
     is how fast it swings to a new heading when the pointer changes course. */
  var TURN_MIN = 0.6;
  var TURN_EASE = 0.3;

  /* A hairline of breathing room around the slot, not a padding factor. */
  var PAD = 1.06;

  var active = null;

  function allowed(event) {
    return (
      event.pointerType === 'mouse' &&
      window.matchMedia(FINE).matches &&
      !window.matchMedia(CALM).matches
    );
  }

  function discFor(media) {
    var disc = media.querySelector('.cc-card__cursor');

    if (!disc) {
      disc = document.createElement('span');
      disc.className = 'cc-card__cursor';
      /* Decoration. It carries no meaning and must not be announced. */
      disc.setAttribute('aria-hidden', 'true');
      media.appendChild(disc);
    }

    return disc;
  }

  /*
    Layout offset up to the card, rather than the difference between two
    viewport rectangles. The disc holds its lock for as long as the pointer
    rests on a size, and a viewport measurement taken once goes stale the moment
    the page scrolls underneath it, which showed up as the disc sitting a few
    pixels below the numeral. Offsets are layout values, so they do not move.
  */
  function offsetWithin(node, ancestor) {
    var x = 0;
    var y = 0;
    var el = node;

    while (el && el !== ancestor) {
      x += el.offsetLeft;
      y += el.offsetTop;
      el = el.offsetParent;
    }

    return { x: x, y: y };
  }

  function start(media, disc, x, y) {
    active = {
      media: media,
      disc: disc,
      /* Where it is, where it is going, and how fast it is getting there. */
      x: x,
      y: y,
      sx: 0.35,
      sy: 0.35,
      tx: x,
      ty: y,
      tsx: 1,
      tsy: 1,
      vx: 0,
      vy: 0,
      vsx: 0,
      vsy: 0,
      rot: 0,
      frame: null
    };

    write(active, 0, 0);
    wake();
  }

  function wake() {
    if (active && !active.frame) active.frame = requestAnimationFrame(step);
  }

  function write(s, stretch, rot) {
    s.disc.style.setProperty('--cc-cursor-x', s.x + 'px');
    s.disc.style.setProperty('--cc-cursor-y', s.y + 'px');
    s.disc.style.setProperty('--cc-cursor-rot', rot + 'rad');
    s.disc.style.setProperty('--cc-cursor-sx', s.sx * (1 + stretch));
    s.disc.style.setProperty('--cc-cursor-sy', s.sy * (1 - stretch));
  }

  function step() {
    var s = active;
    if (!s) return;

    s.frame = null;

    /* Semi-implicit Euler. Accelerate toward the target by the distance
       remaining, bleed off a fraction of the velocity, then move. */
    s.vx = (s.vx + (s.tx - s.x) * STIFF) * DAMP;
    s.vy = (s.vy + (s.ty - s.y) * STIFF) * DAMP;
    s.x += s.vx;
    s.y += s.vy;

    s.vsx = (s.vsx + (s.tsx - s.sx) * SCALE_STIFF) * SCALE_DAMP;
    s.vsy = (s.vsy + (s.tsy - s.sy) * SCALE_STIFF) * SCALE_DAMP;
    s.sx += s.vsx;
    s.sy += s.vsy;

    /*
      Stretch along the direction of travel, not along the screen axes.

      The previous version compared |vx| against |vy| and stretched whichever
      won, which meant a diagonal flick cancelled out and produced no distortion
      at all: the one direction where a drop is most obviously stretched was the
      one where nothing happened. Taking the speed as a magnitude and the
      heading as an angle stretches it correctly on every vector.

      Order matters, and CSS fixes it for us. The individual properties apply as
      translate, then rotate, then scale, so the disc is stretched along its own
      x first and the rotation then points that stretch down the direction of
      travel.
    */
    var speed = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
    var stretch = speed * SQUASH;
    if (stretch > SQUASH_CAP) stretch = SQUASH_CAP;

    /* Below a crawl there is no heading worth having, so it unwinds to level
       rather than freezing at whatever angle it happened to stop on. That
       matters once it is locked to a slot, where the shape is a stadium and a
       few degrees of tilt would be obvious. */
    var want = speed > TURN_MIN ? Math.atan2(s.vy, s.vx) : 0;
    var delta = want - s.rot;

    /*
      Half turns are free. A stretched ellipse looks identical pointed at an
      angle or at that angle plus 180, so both the step and the running total
      are folded into a quarter turn either side of level. Without this the
      heading accumulates: a sweep left then right wound it past 200 degrees,
      and unwinding that to level once the disc settled would have rotated a
      locked stadium through a right angle on the way back.
    */
    while (delta > Math.PI / 2) delta -= Math.PI;
    while (delta < -Math.PI / 2) delta += Math.PI;
    s.rot += delta * TURN_EASE;
    while (s.rot > Math.PI / 2) s.rot -= Math.PI;
    while (s.rot < -Math.PI / 2) s.rot += Math.PI;

    write(s, stretch, s.rot);

    /* Sleep once there is nothing left to do. Any target change wakes it. */
    var moving =
      Math.abs(s.vx) > 0.01 ||
      Math.abs(s.vy) > 0.01 ||
      Math.abs(s.vsx) > 0.0005 ||
      Math.abs(s.vsy) > 0.0005 ||
      Math.abs(s.rot) > 0.004 ||
      Math.abs(s.tx - s.x) > 0.05 ||
      Math.abs(s.ty - s.y) > 0.05;

    if (moving) s.frame = requestAnimationFrame(step);
  }

  function clearHolders(media) {
    var held = media.querySelectorAll('.cc-card__size.is-magnetised');
    var i;

    for (i = 0; i < held.length; i++) {
      held[i].classList.remove('is-magnetised');
    }
  }

  function magnetise(chip, media) {
    if (!active || active.media !== media) return;

    var at = offsetWithin(chip, media);
    /* offsetWidth is the layout size and is not affected by the scale already
       on the element, so the ratio holds whatever the merchant has done to the
       type scale slider. */
    var base = active.disc.offsetWidth || 28;
    var w = chip.offsetWidth * PAD;
    var h = chip.offsetHeight * PAD;

    active.tx = at.x + chip.offsetWidth / 2;
    active.ty = at.y + chip.offsetHeight / 2;
    active.tsx = w / base;
    active.tsy = h / base;

    /*
      A stadium, not an ellipse. border-radius is applied before the transform,
      so a single 50% on a disc scaled unevenly renders as an ellipse that
      pinches to nothing at the ends of a long slot and looks nothing like the
      chip it is sitting on. Dividing the target corner radius by each axis's
      scale cancels the distortion, so what lands is a stadium with the same
      corner radius as the slot: fully round top and bottom, flat down the
      sides. The vertical term always resolves to half the base box; the
      horizontal one is what does the work.
    */
    active.disc.style.borderRadius =
      h / 2 / active.tsx + 'px / ' + h / 2 / active.tsy + 'px';

    clearHolders(media);
    chip.classList.add('is-magnetised');
    media.classList.add('is-magnetised');
    wake();
  }

  function release(media) {
    clearHolders(media);
    media.classList.remove('is-magnetised');

    if (active && active.media === media) {
      active.tsx = 1;
      active.tsy = 1;
      active.disc.style.borderRadius = '50%';
      wake();
    }
  }

  function pullableSize(target) {
    var chip = target.closest('.cc-card__size');
    if (!chip || chip.classList.contains('cc-card__size--out')) return null;
    return chip;
  }

  document.addEventListener('pointerover', function (event) {
    if (!allowed(event)) return;

    var media = event.target.closest('.cc-card__media');
    if (!media) return;

    var disc = discFor(media);

    /* First frame on this card. The disc keeps its last position between
       visits, so springing it from there would send it sailing across the
       photograph from wherever it was abandoned. Seed the spring at the
       pointer instead, with no velocity, so it simply appears. */
    if (!active || active.media !== media) {
      if (active) active.frame = null;
      var box = media.getBoundingClientRect();
      start(media, disc, event.clientX - box.left, event.clientY - box.top);
    }

    media.classList.add('is-cursor-on');

    var chip = pullableSize(event.target);
    if (chip) {
      magnetise(chip, media);
    } else {
      release(media);
    }
  });

  document.addEventListener('pointerout', function (event) {
    var media = event.target.closest('.cc-card__media');
    if (!media) return;

    /* pointerout also fires moving between children of the card. Only treat it
       as a real exit when the pointer has actually left the media box. */
    if (event.relatedTarget && media.contains(event.relatedTarget)) return;

    media.classList.remove('is-cursor-on');
    release(media);

    if (active && active.media === media) {
      active.frame = null;
      active = null;
    }
  });

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!allowed(event)) return;
      if (!active) return;

      var media = event.target.closest('.cc-card__media');
      if (!media || media !== active.media) return;

      /* Held by a size chip. The chip owns the target now, so moving the
         pointer around inside it must not drag the disc back off centre. */
      if (media.classList.contains('is-magnetised')) return;

      var box = media.getBoundingClientRect();
      active.tx = event.clientX - box.left;
      active.ty = event.clientY - box.top;
      wake();
    },
    { passive: true }
  );
})();
